import { ApiTicket, CoiSpecificData } from '../../types';
import { getFormMapper, AnswerData } from '../../utils/formMapper';

function buildAnswersData(apiTicket: ApiTicket): AnswerData[] {
  return apiTicket.answers?.map(answer => ({
    questionTranslationKey: answer.question.questionTranslationKey,
    answer: answer.answer,
    selectedOption: answer.selectedOption
  })) || [];
}

function buildCoiSpecificData(extractedFields: Record<string, string>, subcategory: string | null): CoiSpecificData {
  const baseData: CoiSpecificData = {
    otherPartyInvolved: extractedFields['Other Party Involved'] || 'N/A',
    relationshipType: extractedFields['Relationship Type'] || 'N/A',
    subcategory,
    extractedFields,
    detectedFields: Object.keys(extractedFields)
  };

  baseData.partyType = extractedFields['Party Type'];
  baseData.companyName = extractedFields['Company Name'];
  baseData.jobTitle = extractedFields['Job title of the other party involved'];
  baseData.organizationName = extractedFields['Organization Name'];
  baseData.description = extractedFields.Description;
  baseData.awareNegotiations = extractedFields['Are You aware...?'];
  baseData.involvedNegotiations = extractedFields['Are You involved...?'];
  baseData.occupation = extractedFields['What is the other occupation?'];
  baseData.employmentType = extractedFields['Will you be working for another company or will you be self-employed?'];
  baseData.isCompetitor = extractedFields['Is this company:'];
  baseData.foundThroughABI = extractedFields['Was this opportunity found through ABI?'];

  return baseData;
}

export function mapCoiSpecificData(apiTicket: ApiTicket): CoiSpecificData {
  const coiMapper = getFormMapper('coi');
  
  if (!coiMapper) {
    return {
      otherPartyInvolved: 'N/A',
      relationshipType: 'N/A',
      subcategory: null
    };
  }

  const answersData = buildAnswersData(apiTicket);
  const subcategory = coiMapper.detectCategory(answersData);
  
  if (!subcategory) {
    return {
      otherPartyInvolved: 'N/A',
      relationshipType: 'N/A',
      subcategory: null
    };
  }

  const extractedFields = coiMapper.extractFields(answersData, subcategory);
  return buildCoiSpecificData(extractedFields, subcategory);
}

export function generateCoiDescription(apiTicket: ApiTicket): string {
  if (apiTicket.approvalComments?.trim()) {
    return apiTicket.approvalComments;
  }
  
  const answersData = buildAnswersData(apiTicket);
  const descriptionAnswer = answersData.find(answer => 
    answer.questionTranslationKey.includes('.details.question')
  );
  
  if (descriptionAnswer?.answer?.trim()) {
    return descriptionAnswer.answer;
  }
  
  const formTypeName = apiTicket.formType.name || 'Compliance matter';
  const zone = apiTicket.requestedBy.zone?.name || 'unknown zone';
  return `${formTypeName} declared by employee in the ${zone}.`;
}
